import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";

import { crmPostventaApi } from "./crmPostventaApi";

const emptyForm = {
  id: 0,
  nombreCampania: "",
  mensaje: "",
  fechaInicio: "",
  fechaFin: "",
  activa: true,
};

const formatDate = (value) => String(value || "").slice(0, 10);

const rangesOverlap = (left, right) => {
  const leftStart = new Date(left.fechaInicio);
  const leftEnd = new Date(left.fechaFin);
  const rightStart = new Date(right.fechaInicio);
  const rightEnd = new Date(right.fechaFin);
  return leftStart <= rightEnd && rightStart <= leftEnd;
};

const ZocoCheckCampaignsPanel = ({ darkMode, isAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [statusFilter, setStatusFilter] = useState("todas");
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const data = await crmPostventaApi.obtenerZocoCheckCampanias();
      const items = Array.isArray(data) ? data : [];
      setCampaigns(items);
      setActiveCampaign(items.find((item) => item?.vigente) || null);
    } catch (error) {
      Swal.fire("Campanas", error.message || "No se pudieron cargar las campanas.", "error");
      setCampaigns([]);
      setActiveCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  const filteredCampaigns = useMemo(() => {
    if (statusFilter === "todas") {
      return campaigns;
    }

    return campaigns.filter((item) => String(item?.estado || "").toLowerCase() === statusFilter);
  }, [campaigns, statusFilter]);

  const openCreate = () => {
    setForm({
      ...emptyForm,
      fechaInicio: new Date().toISOString().slice(0, 10),
      fechaFin: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item?.id || 0,
      nombreCampania: item?.nombreCampania || "",
      mensaje: item?.mensaje || "",
      fechaInicio: formatDate(item?.fechaInicio),
      fechaFin: formatDate(item?.fechaFin),
      activa: Boolean(item?.activa),
    });
    setOpen(true);
  };

  const validateForm = () => {
    if (!form.nombreCampania.trim() || !form.mensaje.trim() || !form.fechaInicio || !form.fechaFin) {
      Swal.fire("Campanas", "Complete nombre, mensaje y rango de fechas.", "info");
      return false;
    }

    if (form.fechaFin < form.fechaInicio) {
      Swal.fire("Campanas", "La fecha fin no puede ser anterior a la fecha inicio.", "info");
      return false;
    }

    const overlapped = campaigns.find(
      (item) =>
        Number(item?.id || 0) !== Number(form.id || 0) &&
        Boolean(item?.activa) &&
        rangesOverlap(
          { fechaInicio: form.fechaInicio, fechaFin: form.fechaFin },
          { fechaInicio: formatDate(item?.fechaInicio), fechaFin: formatDate(item?.fechaFin) }
        )
    );

    if (overlapped) {
      Swal.fire("Campanas", `La campana se solapa con "${overlapped.nombreCampania}".`, "warning");
      return false;
    }

    return true;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const result = await crmPostventaApi.guardarZocoCheckCampania({
        Id: form.id,
        NombreCampania: form.nombreCampania,
        Mensaje: form.mensaje,
        FechaInicio: form.fechaInicio,
        FechaFin: form.fechaFin,
        Activa: form.activa,
      });
      setOpen(false);
      await loadCampaigns();
      Swal.fire("Campanas", result?.message || "Campana guardada correctamente.", "success");
    } catch (error) {
      Swal.fire("Campanas", error.message || "No se pudo guardar la campana.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleState = async (item) => {
    try {
      const result = await crmPostventaApi.cambiarEstadoZocoCheckCampania(item.id, !item.activa);
      await loadCampaigns();
      Swal.fire("Campanas", result?.message || "Estado actualizado correctamente.", "success");
    } catch (error) {
      Swal.fire("Campanas", error.message || "No se pudo cambiar el estado.", "error");
    }
  };

  const handleDelete = async (item) => {
    const confirmation = await Swal.fire({
      title: "Eliminar campana",
      text: `Se eliminara ${item?.nombreCampania || "la campana seleccionada"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      const result = await crmPostventaApi.eliminarZocoCheckCampania(item.id);
      await loadCampaigns();
      Swal.fire("Campanas", result?.message || "Campana eliminada correctamente.", "success");
    } catch (error) {
      Swal.fire("Campanas", error.message || "No se pudo eliminar la campana.", "error");
    }
  };

  const handleBootstrap = async () => {
    setBootstrapLoading(true);
    try {
      const data = await crmPostventaApi.bootstrapZocoCheck(new Date().toISOString().slice(0, 10));
      const created = Number(data?.flujosCreados ?? data?.comerciosCreados ?? 0);
      await Swal.fire({
        title: "Bootstrap Zoco Check",
        html: [
          data?.message || "",
          `Comercios evaluados: ${data?.comerciosEvaluados ?? 0}`,
          `Elegibles: ${data?.comerciosElegibles ?? 0}`,
          `Creados: ${created}`,
          `Ya existentes: ${data?.yaExistentes ?? data?.duplicadosEvitados ?? 0}`,
          `Excluidos: ${data?.comerciosExcluidos ?? 0}`,
          `Excluidos por facturación: ${data?.excluidosPorFacturacion ?? 0}`,
          `Excluidos por onboarding: ${data?.excluidosPorOnboarding ?? 0}`,
          `Excluidos por retención: ${data?.excluidosPorRetencion ?? 0}`,
          `Excluidos por reclamo crítico: ${data?.excluidosPorReclamoCritico ?? 0}`,
          `Legacy excluidos de la tabla: ${data?.legacyExcluidos ?? 0}`,
          `Errores/advertencias: ${data?.cantidadErrores ?? data?.warnings?.length ?? 0}`,
        ].join("<br/>"),
        icon: created > 0 ? "success" : "warning",
      });
    } catch (error) {
      Swal.fire("Bootstrap", error.message || "No se pudo ejecutar el bootstrap.", "error");
    } finally {
      setBootstrapLoading(false);
    }
  };

  return (
    <div className="d-grid gap-4">
      <section className={`crm-postventa-panel ${darkMode ? "is-dark" : ""}`}>
        <div className="crm-postventa-panel-title">
          <div>
            <h3>Campanas Zoco Check</h3>
            <span className="crm-postventa-card-label">
              Vigente: {activeCampaign?.nombreCampania || "No hay campana vigente"}
            </span>
          </div>
          {isAdmin ? (
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="outline-success" onClick={handleBootstrap} disabled={bootstrapLoading}>
                {bootstrapLoading ? "Generando..." : "Generar Zoco Check del mes"}
              </Button>
              <Button variant="success" onClick={openCreate}>
                Nueva campana
              </Button>
            </div>
          ) : null}
        </div>

        {!isAdmin ? (
          <Alert variant="secondary" className="mb-0">
            Esta seccion esta disponible solo para perfiles administrativos/comerciales.
          </Alert>
        ) : (
          <>
            <div className="crm-postventa-zoco-campaign-toolbar">
              <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="todas">Todas</option>
                <option value="vigente">Vigentes</option>
                <option value="futura">Futuras</option>
                <option value="vencida">Vencidas</option>
              </Form.Select>
            </div>

            {loading ? (
              <div className="crm-postventa-subtle"><Spinner animation="border" size="sm" className="me-2" />Cargando campanas...</div>
            ) : (
              <div className="crm-postventa-alert-queue-scroll">
                <Table className="crm-postventa-table mb-0" hover>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Estado</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Activa</th>
                      <th>Mensaje</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCampaigns.length ? filteredCampaigns.map((item) => (
                      <tr key={item.id}>
                        <td>{item.nombreCampania || "-"}</td>
                        <td>{item.estado || "-"}</td>
                        <td>{formatDate(item.fechaInicio) || "-"}</td>
                        <td>{formatDate(item.fechaFin) || "-"}</td>
                        <td>{item.activa ? "Si" : "No"}</td>
                        <td className="crm-postventa-zoco-campaign-message">{item.mensaje || "-"}</td>
                        <td>
                          <div className="crm-postventa-zoco-actions">
                            <Button size="sm" variant="outline-secondary" onClick={() => openEdit(item)}>
                              Editar
                            </Button>
                            <Button size="sm" variant="outline-success" onClick={() => handleToggleState(item)}>
                              {item.activa ? "Desactivar" : "Activar"}
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item)}>
                              Eliminar
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-muted">
                          No hay campanas para el filtro seleccionado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </section>

      <Modal show={open} onHide={() => setOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{form.id ? "Editar campana" : "Nueva campana"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <div className="crm-postventa-form-grid">
              <Form.Group>
                <Form.Label>Nombre</Form.Label>
                <Form.Control value={form.nombreCampania} onChange={(event) => setForm((prev) => ({ ...prev, nombreCampania: event.target.value }))} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha inicio</Form.Label>
                <Form.Control type="date" value={form.fechaInicio} onChange={(event) => setForm((prev) => ({ ...prev, fechaInicio: event.target.value }))} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Fecha fin</Form.Label>
                <Form.Control type="date" value={form.fechaFin} onChange={(event) => setForm((prev) => ({ ...prev, fechaFin: event.target.value }))} />
              </Form.Group>
              <Form.Group className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  id="zoco-check-campania-activa"
                  label="Campana activa"
                  checked={form.activa}
                  onChange={(event) => setForm((prev) => ({ ...prev, activa: event.target.checked }))}
                />
              </Form.Group>
              <Form.Group className="crm-postventa-form-grid-span-2">
                <Form.Label>Mensaje</Form.Label>
                <Form.Control as="textarea" rows={6} value={form.mensaje} onChange={(event) => setForm((prev) => ({ ...prev, mensaje: event.target.value }))} />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="success" disabled={saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ZocoCheckCampaignsPanel;
